import { useParams, useNavigate, useOutletContext, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DamageOverview } from './damage-overview';
import { Spinner } from '@/components/ui/spinner';
import { getDamage } from '@/queries/damages/get-damage';
import { ROLE } from '@/common/role';
import type { DamageType } from '@/common/damage';
import type { WithDeletedBy } from '@/common/soft-delete';
import { DeletedItemBanner } from '@/components/soft-delete/DeletedItemBanner';
import { RestoreItemButton } from '@/components/soft-delete/RestoreItemButton';
import { ReadOnlySubtree } from '@/context/AssetReadOnlyContext';
import { useDeletedPreview } from '@/hooks/use-deleted-preview';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';

export function DamageDetail() {
	const { t } = useTranslation();
	const { mapActions, activeDamage } = useOutletContext<{
		mapActions: (action: { type: string; payload: any }) => void;
		activeDamage: any;
	}>();

	const { inspectionId, assetId, damageId } = useParams<{
		inspectionId: string;
		assetId: string;
		damageId: string;
	}>();

	const navigate = useNavigate();
	const location = useLocation();

	const isNewDamage = damageId === 'new';

	// Check for duplicated damage data passed via navigation state
	const duplicatedDamage = location.state?.duplicatedDamage;
	// A deleted damage opened from the deleted list: read-only, overview only (GH #331)
	const isDeleted = useDeletedPreview('damage');
	const {
		data: damage,
		isLoading,
		isError,
	} = useQuery({
		queryKey: isDeleted
			? ['damage', assetId, inspectionId, damageId, 'deleted']
			: ['damage', assetId, inspectionId, damageId],
		queryFn: () => getDamage(damageId!, { deleted: isDeleted }),
		enabled: !!damageId && !isNewDamage && !!assetId && !!inspectionId,
	});

	const onBack = () => {
		if (isNewDamage) {
			mapActions({
				type: 'cancel',
				payload: {},
			});
			activeDamage({});
		}
		navigate(`/assets/${assetId}/inspections/${inspectionId}?tab=damages`);
	};

	if (isLoading)
		return (
			<div className="h-full flex items-center justify-center">
				<Spinner />
			</div>
		);

	// Before the not-found check: a failed request also leaves `damage` empty, and
	// an outage must not read as a deleted record.
	if (isError)
		return (
			<div className="h-full flex items-center justify-center text-red-500">
				{t('damages.detail.loadError')}
			</div>
		);

	if (!isLoading && !damage && !isNewDamage && !duplicatedDamage)
		return <div className="p-6">{t('damages.detail.notFound')}</div>;

	const deletedDamage = isDeleted ? (damage as WithDeletedBy<DamageType> | null | undefined) : null;
	return (
		<ReadOnlySubtree active={isDeleted}>
			<DamageOverview
				key={isNewDamage ? 'new-damage' : damageId}
				damage={duplicatedDamage || damage || null}
				isNew={isNewDamage}
				assetId={assetId!}
				inspectionId={inspectionId!}
				onBack={onBack}
				deletedBanner={
					deletedDamage ? (
						<DeletedItemBanner
							className="mx-4 mb-3"
							itemLabel={t('common.entities.damage', { count: 1 })}
							deletedAt={deletedDamage.deletedAt}
							deletedBy={deletedDamage.deletedBy}
						/>
					) : undefined
				}
				restoreButton={
					deletedDamage ? (
						<RestoreItemButton
							target={softDeleteTargets.damage(damageId as string)}
							name={deletedDamage.title || t('common.entities.damage', { count: 1 })}
							restoreRole={ROLE.DAMAGES_DELETE}
							onRestored={() =>
								navigate(`/assets/${assetId}/inspections/${inspectionId}/damages/${damageId}`, { replace: true })
							}
						/>
					) : undefined
				}
			/>
		</ReadOnlySubtree>
	);
}
